// src/components/Navbar.jsx
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FaBars, FaTimes, FaEnvelopeOpenText, FaVolumeUp, FaVolumeMute, FaAdjust, FaTextHeight } from "react-icons/fa";
import API from "../api/api";
import { resolveAssetUrl } from "../api/config";

const FALLBACK_MENU = [
  { name: "Home", path: "/" },
  { name: "Sports & Events", path: "/events" },
  { name: "Gallery", path: "/gallery" },
  { name: "News", path: "/news" },
  { name: "Messages", path: "/messages" },
  { name: "Donate", path: "/donate" },
  { name: "Volunteer", path: "/volunteer" },
  { name: "About Us", path: "/about-us" },
  { name: "Committee Members", path: "/committee-members" },
  { name: "Disclosures", path: "/disclosures" },
];

const HEADER_SECTION_KEYS = [
  "site_header_president",
  "site_header_leader",
  "site_header_profile",
];

const FALLBACK_HEADER_PROFILE = {
  name: "Sri Gonuguntla Koteswara Rao",
  lines: ["President - Para Sports Association of Andhra Pradesh"],
  imageSrc: "/images/president.jpg",
};

const HEADER_SHELL_CLASS =
  "relative lg:sticky lg:top-0 z-50 border-b border-emerald-400 bg-gradient-to-r from-emerald-300/95 via-teal-200/90 to-cyan-200/90 lg:backdrop-blur lg:supports-[backdrop-filter]:bg-emerald-200/95 shadow-[0_10px_24px_rgba(5,150,105,0.22)]";

const HEADER_SURFACE_CLASS =
  "bg-gradient-to-r from-emerald-300/95 via-teal-200/90 to-cyan-200/90 lg:backdrop-blur lg:supports-[backdrop-filter]:bg-emerald-200/95";

const WEBMAIL_URL = "https://webmail.apparasports.com";

const ADMIN_MENU = [
  { name: "Home", path: "/", roles: ["admin", "secretary", "district"] },
  { name: "Dashboard", path: "/admin", roles: ["admin", "secretary", "district"] },
  { name: "Public Achievement Visibility", path: "/admin/public-achievements", roles: ["admin", "secretary"] },
];

const NavLink = ({ children, active = false, ...props }) => (
  <button
    className={`px-2.5 py-1.5 rounded-full text-[11px] xl:text-xs 2xl:text-sm font-semibold whitespace-nowrap transition-all duration-300 ${active
      ? "bg-emerald-200 text-emerald-900 shadow-sm"
      : "text-slate-700 hover:text-emerald-900 hover:bg-emerald-100"
      }`}
    {...props}
  >
    {children}
  </button>
);

const splitTextLines = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location?.pathname || "/";
  const { user, token, logout } = useContext(AuthContext);
  const [menuItems, setMenuItems] = useState(FALLBACK_MENU);
  const [headerSections, setHeaderSections] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const dropdownRef = useRef(null);

  // Accessibility States
  const [textScale, setTextScale] = useState(() => localStorage.getItem("access_text_scale") || "normal");
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem("access_high_contrast") === "true");
  const [readingLang, setReadingLang] = useState(null);

  useEffect(() => {
    document.documentElement.classList.remove("accessible-normal", "accessible-large", "accessible-extra-large");
    document.documentElement.classList.add(`accessible-${textScale}`);
    localStorage.setItem("access_text_scale", textScale);
  }, [textScale]);

  useEffect(() => {
    if (highContrast) {
      document.body.classList.add("high-contrast");
    } else {
      document.body.classList.remove("high-contrast");
    }
    localStorage.setItem("access_high_contrast", String(highContrast));
  }, [highContrast]);

  const stopReading = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setReadingLang(null);
  };

  const startReading = (lang) => {
    if (!window.speechSynthesis) {
      return;
    }
    stopReading();
    setReadingLang(lang);

    const getPageSummary = (path, l) => {
      const p = String(path || "").toLowerCase();
      if (p.includes("excellence-awards")) {
        if (l === "en") {
          return "National Sports Day Para Sports Excellence Awards page. You can apply for Best Para Coach, Best Junior and Senior Para Sportsman, and other categories. Submission deadline is 15th August 2026. The ceremony will be held on 5th September 2026 at Visakhapatnam. To apply, complete the application form at the bottom of the page or scan the QR Code on the poster.";
        } else {
          return "జాతీయ క్రీడా దినోత్సవం పారా క్రీడల అవార్డులు. దరఖాస్తు చేసుకోవడానికి చివరి తేదీ 15 ఆగస్టు 2026. అవార్డుల ప్రదానోత్సవం 10 సెప్టెంబర్ 2026 న విశాఖపట్నంలో జరుగుతుంది. పారా క్రీడాకారులు, కోచ్‌లు దరఖాస్తు చేసుకోవచ్చు. దరఖాస్తు చేయడానికి కింద ఉన్న దరఖాస్తు ఫారమ్ ని పూర్తి చేసి సమర్పించండి.";
        }
      }
      if (p === "/" || p === "/home" || p.includes("home")) {
        if (l === "en") {
          return "Welcome to the Para Sports Association of Andhra Pradesh portal. We promote Paralympic sports and empower differently-abled athletes. Explore our open events, view latest news, and register to represent your district.";
        } else {
          return "ఆంధ్రప్రదేశ్ పారా స్పోర్ట్స్ అసోసియేషన్ పోర్టల్‌కు స్వాగతం. మేము పారా క్రీడలను ప్రోత్సహిస్తాము మరియు క్రీడాకారులను సపోర్ట్ చేస్తాము. తాజా వార్తలను తెలుసుకోండి మరియు రిజిస్టర్ అవ్వండి.";
        }
      }
      // Fallback
      const mainContent = document.querySelector("main") || document.querySelector(".accessible-content") || document.body;
      if (mainContent) {
        const items = Array.from(mainContent.querySelectorAll("h1, h2, h3, p"))
          .map(el => el.innerText.trim())
          .filter(t => t.length > 15 && !t.includes("Accessibility"))
          .slice(0, 8);
        if (items.length > 0) {
          return items.join(". ");
        }
      }
      return l === "en" ? "This is the Para Sports Association portal." : "ఇది పారా స్పోర్ట్స్ అసోసియేషన్ పోర్టల్.";
    };

    const speechText = getPageSummary(window.location.pathname, lang);
    const utterance = new SpeechSynthesisUtterance(speechText);
    utterance.lang = lang === "en" ? "en-US" : "te-IN";
    utterance.rate = 0.85;

    utterance.onend = () => {
      setReadingLang(null);
    };
    utterance.onerror = () => {
      setReadingLang(null);
    };

    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Fetch menu items
  useEffect(() => {
    let mounted = true;
    const fetchMenu = async () => {
      try {
        const endpoint = token ? "/menu" : "/menu/public";
        const res = await API.get(endpoint);
        if (mounted) {
          const normalizedMenu = Array.isArray(res?.menu)
            ? res.menu
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res)
                ? res
                : [];
          setMenuItems(
            normalizedMenu.length > 0 ? normalizedMenu : FALLBACK_MENU
          );
        }
      } catch (err) {
        console.error("Menu load failed", err);
        if (mounted) setMenuItems(FALLBACK_MENU);
      }
    };
    fetchMenu();
    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    let mounted = true;
    const fetchHeaderSections = async () => {
      try {
        const rows = await API.fetchAboutSections("site_header");
        if (!mounted) return;
        setHeaderSections(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error("Header content load failed", err);
        if (mounted) setHeaderSections([]);
      }
    };
    fetchHeaderSections();
    return () => {
      mounted = false;
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [announcements, setAnnouncements] = useState([]);

  // Fetch announcements
  useEffect(() => {
    let mounted = true;
    const fetchAnnouncements = async () => {
      try {
        const res = await API.get("/public/announcements");
        if (mounted) {
          const items = Array.isArray(res?.announcements)
            ? res.announcements
            : Array.isArray(res)
              ? res
              : [];
          setAnnouncements(items);
        }
      } catch (err) {
        console.error("Announcements load failed", err);
      }
    };
    fetchAnnouncements();
    return () => {
      mounted = false;
    };
  }, []);

  const handleAnnounceClick = (e, item) => {
    e.stopPropagation();
    if (item?.link_url) {
      if (item.link_url.startsWith("http")) {
        window.open(item.link_url, "_blank");
      } else {
        navigate(item.link_url);
      }
    }
  };

  // Body scroll lock for mobile menu
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    if (mobileMenu) {
      body.style.overflow = "hidden";
      html.style.overflow = "hidden";
    } else {
      body.style.overflow = "";
      html.style.overflow = "";
    }
    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [mobileMenu]);

  useEffect(() => {
    setMobileMenu(false);
    setShowDropdown(false);
  }, [currentPath]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) {
        setMobileMenu(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  const displayName = user?.name || "User";
  const role = (user?.role || "player").toString().toLowerCase();
  const isLoggedIn = Boolean(token);
  const dashboardPath = role === "district" ? "/district" : ["admin", "secretary"].includes(role) ? "/admin" : "/player";
  const isAdminArea = /^\/(admin|district)(\/|$)/.test(currentPath);
  const adminMenuItems = useMemo(() => {
    const prefix = role === "district" ? "/district" : "/admin";
    return ADMIN_MENU.filter((item) => item.roles.includes(role)).map((item) => ({
      ...item,
      path: item.path === "/admin" ? prefix : item.path,
    }));
  }, [role]);
  const parsedMenuItems = Array.isArray(menuItems)
    ? menuItems.filter(
      (item) =>
        item &&
        typeof item.name === "string" &&
        item.name.trim() !== "" &&
        typeof item.path === "string" &&
        item.path.trim() !== ""
    )
    : [];
  const safeMenuItems =
    parsedMenuItems.length > 0 ? parsedMenuItems : FALLBACK_MENU;
  const navItems = isAdminArea ? adminMenuItems : safeMenuItems;
  const publicNavItems = useMemo(() => {
    if (isAdminArea) return navItems;
    const take = (name) => navItems.find((item) => item.name.toLowerCase() === name.toLowerCase());
    const hidden = ["events", "sports & events", "updates & results", "players", "partner", "partners", "about us", "coach profiles", "committee members", "messages", "myas disclosure", "disclosures"];
    const base = navItems.filter((item) => !hidden.includes(item.name.toLowerCase()));
    const sports = [take("Events"), take("Updates & Results")].filter(Boolean);
    const about = [take("About Us"), take("Committee Members"), take("MYAS Disclosure") || take("Disclosures"), take("Messages"), take("Players"), take("Partner") || take("Partners")].filter(Boolean);
    const at = Math.max(0, base.findIndex((item) => item.name.toLowerCase() === "gallery"));
    base.splice(at, 0, { name: "Sports & Events", path: sports[0]?.path || "/events", children: sports });
    base.push({ name: "About", path: about[0]?.path || "/about-us", children: about });
    return base;
  }, [isAdminArea, navItems]);

  const headerSection = useMemo(() => {
    const activeSections = (Array.isArray(headerSections) ? headerSections : []).filter(
      (section) => section?.is_active !== false
    );
    return (
      HEADER_SECTION_KEYS
        .map((key) =>
          activeSections.find(
            (section) => String(section?.section_key || "").trim().toLowerCase() === key
          )
        )
        .find(Boolean) ||
      activeSections[0] ||
      null
    );
  }, [headerSections]);
  const headerName = String(headerSection?.heading || "").trim() || FALLBACK_HEADER_PROFILE.name;
  const headerLines = [
    String(headerSection?.subheading || "").trim(),
    ...splitTextLines(headerSection?.body),
  ]
    .filter(Boolean)
    .filter(
      (line) =>
        !/affiliated to paralympic committee of india/i.test(String(line || ""))
    );
  const resolvedHeaderLines =
    headerLines.length > 0 ? headerLines : FALLBACK_HEADER_PROFILE.lines;
  const isItemActive = (path) => {
    const cleanPath = String(path || "").split("?")[0];
    if (!cleanPath) return false;
    return (
      currentPath === cleanPath ||
      (cleanPath !== "/" && currentPath.startsWith(`${cleanPath}/`))
    );
  };
  const photoUrl = resolveAssetUrl(user?.photo, {
    folder: "players",
    entityId: user?.id,
    defaultPath: "/images/placeholder-user.svg",
  });
  const headerPhotoUrl = resolveAssetUrl(headerSection?.image_path || "", {
    folder: "page-sections",
    defaultPath: FALLBACK_HEADER_PROFILE.imageSrc,
  });

  if (isAdminArea) {
    return (
      <header className={HEADER_SHELL_CLASS}>
        {/* ===== ACCESSIBILITY BAR ===== */}
        <div className={`py-2 px-4 border-b ${highContrast ? 'bg-black border-white text-white' : 'bg-slate-100 border-slate-200 text-slate-700'} transition-all`}>
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3 font-sans text-xs font-semibold">
            {/* <div className="hidden md:flex items-center gap-1.5">
            <span role="img" aria-label="accessibility">♿</span>
            <span className="font-extrabold uppercase tracking-wide">Accessibility:</span>
          </div> */}

            <div className="flex flex-wrap items-center gap-3.5">
              {/* Text Size Scale */}
              <div className="flex items-center gap-1">
                <FaTextHeight className="opacity-75" />
                <button
                  type="button"
                  onClick={() => setTextScale("normal")}
                  className={`px-2 py-0.5 rounded text-[10px] md:text-xs transition-all ${textScale === "normal" ? (highContrast ? "bg-white text-black font-black border border-white" : "bg-emerald-600 text-white font-extrabold shadow-sm") : (highContrast ? "bg-black text-white border border-white" : "bg-white hover:bg-slate-200 border border-slate-300 text-slate-800")}`}
                >
                  A
                </button>
                <button
                  type="button"
                  onClick={() => setTextScale("large")}
                  className={`px-2 py-0.5 rounded text-[10px] md:text-xs transition-all ${textScale === "large" ? (highContrast ? "bg-white text-black font-black border border-white" : "bg-emerald-600 text-white font-extrabold shadow-sm") : (highContrast ? "bg-black text-white border border-white" : "bg-white hover:bg-slate-200 border border-slate-300 text-slate-800")}`}
                >
                  A+
                </button>
                <button
                  type="button"
                  onClick={() => setTextScale("extra-large")}
                  className={`px-2 py-0.5 rounded text-[10px] md:text-xs transition-all ${textScale === "extra-large" ? (highContrast ? "bg-white text-black font-black border border-white" : "bg-emerald-600 text-white font-extrabold shadow-sm") : (highContrast ? "bg-black text-white border border-white" : "bg-white hover:bg-slate-200 border border-slate-300 text-slate-800")}`}
                >
                  A++
                </button>
              </div>

              {/* High Contrast Mode */}
              <button
                type="button"
                onClick={() => setHighContrast(!highContrast)}
                className={`flex items-center gap-1 px-2.5 py-0.5 rounded border text-[10px] md:text-xs transition-all ${highContrast ? "bg-yellow-400 text-black border-yellow-400 font-bold" : "bg-white hover:bg-slate-200 border-slate-300 text-slate-800"}`}
              >
                <FaAdjust />
                {highContrast ? "Contrast: On" : "Contrast: Off"}
              </button>

              {/* Text To Speech Read Aloud */}
              <div className="flex items-center gap-1">
                {readingLang ? (
                  <>
                    <span className="text-emerald-600 animate-pulse font-extrabold flex items-center gap-1 text-[10px] bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                      <FaVolumeUp className="animate-bounce" /> Reading {readingLang === "en" ? "EN" : "తెలుగు"}
                    </span>
                    <button
                      type="button"
                      onClick={stopReading}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-bold transition-all shadow-sm"
                    >
                      <FaVolumeMute /> Stop
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => startReading("en")}
                      className={`flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] transition-all font-bold ${highContrast ? "border border-white text-white hover:bg-white hover:text-black" : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"}`}
                    >
                      🔊 EN
                    </button>
                    <button
                      type="button"
                      onClick={() => startReading("te")}
                      className={`flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] transition-all font-bold ${highContrast ? "border border-white text-white hover:bg-white hover:text-black" : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"}`}
                    >
                      🔊 తెలుగు
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex justify-between items-center gap-2 sm:gap-3 px-[clamp(0.5rem,2vw,2rem)] py-2.5 sm:py-3">
          <div
            className="min-w-0 flex items-center gap-2 sm:gap-3 cursor-pointer"
            onClick={() => navigate(isLoggedIn ? dashboardPath : "/")}
          >
            <div className="h-[clamp(2.2rem,4vw,3.2rem)] w-auto rounded-none bg-transparent border-0 shadow-none flex items-center justify-center shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                alt="Logo"
                className="h-full w-auto object-contain"
              />
            </div>
            <div>
              <p className="text-[clamp(0.62rem,0.95vw,0.95rem)] font-bold text-slate-900 leading-tight max-w-[clamp(12rem,22vw,24rem)]">
                Para Sports Association of Andhra Pradesh
              </p>
              <p className="text-[9px] font-semibold text-emerald-800 uppercase tracking-wider">
                Admin Workspace
              </p>
            </div>
          </div>

          <nav className="hidden xl:flex items-center gap-1.5 bg-white/95 border border-emerald-200 rounded-full px-2.5 py-1.5 shadow-sm">
            {navItems.map((item, idx) => (
              <NavLink
                key={item.path || `${item.name || "menu"}-${idx}`}
                active={isItemActive(item.path)}
                onClick={() => navigate(item.path)}
              >
                {item.name}
              </NavLink>
            ))}

            {token ? (
              <div className="relative" ref={dropdownRef}>
                <div
                  onClick={() => setShowDropdown((s) => !s)}
                  className="flex items-center gap-3 cursor-pointer rounded-full pl-2 pr-3 py-1.5 hover:bg-emerald-100 transition-colors"
                >
                  <img
                    src={photoUrl}
                    alt="User"
                    className="w-10 h-10 rounded-full object-cover border-2 border-emerald-300"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/placeholder-user.svg";
                    }}
                  />
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900">{displayName}</div>
                    <div className="text-xs text-gray-500 capitalize">{role}</div>
                  </div>
                </div>

                {showDropdown && (
                  <div className="absolute right-0 mt-3 bg-white text-black rounded-2xl shadow-xl w-56 p-2 border border-emerald-200">
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        if (role === "player") navigate("/player-profile");
                        else if (["admin", "secretary", "district"].includes(role)) {
                          navigate(role === "district" ? "/district/profile" : "/admin/profile");
                        }
                        else navigate("/player-profile");
                      }}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                    >
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate("/change-password");
                      }}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors"
                    >
                      Change Password
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 rounded-xl hover:bg-rose-50 text-rose-600 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <button
                  onClick={() => navigate("/login")}
                  className="font-semibold text-emerald-900 border border-emerald-300 bg-emerald-100 rounded-full px-4 py-2 hover:bg-emerald-200 transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full px-5 py-2 hover:from-emerald-700 hover:to-teal-700 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  Register as Player
                </button>
              </div>
            )}
          </nav>

          <button
            className="xl:hidden shrink-0 text-slate-700 bg-white border border-emerald-200 rounded-xl p-2 shadow-sm"
            onClick={() => setMobileMenu((prev) => !prev)}
            aria-label={mobileMenu ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenu}
            aria-controls="mobile-nav-drawer"
          >
            {mobileMenu ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>

        {mobileMenu ? (
          <div className="xl:hidden fixed inset-0 z-[9999] bg-white">
            <div className="h-full flex flex-col">
              <div className="shrink-0 border-b border-emerald-200 bg-gradient-to-r from-emerald-100 to-teal-50 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                    alt="Logo"
                    className="h-10 w-10 rounded-full object-cover border border-emerald-200"
                  />
                  <h2 className="text-2xl font-extrabold text-slate-800 truncate">Menu</h2>
                </div>
                <button
                  onClick={() => setMobileMenu(false)}
                  aria-label="Close menu"
                  className="rounded-lg p-2 hover:bg-emerald-200 transition-colors"
                >
                  <FaTimes size={22} />
                </button>
              </div>

              <nav id="mobile-nav-drawer" className="flex-1 min-h-0 overflow-y-auto px-4 py-4 pb-20">
                <div className="flex flex-col gap-1">
                  {navItems && navItems.length > 0 ? (
                    navItems.map((item, idx) => (
                      <button
                        key={item.path || `${item.name || "menu"}-${idx}`}
                        onClick={() => {
                          setMobileMenu(false);
                          navigate(item.path);
                        }}
                        className="w-full text-left text-lg font-semibold py-2 px-3 rounded-xl border border-transparent hover:border-emerald-200 hover:text-emerald-900 hover:bg-emerald-50 transition-colors"
                      >
                        {item.name}
                      </button>
                    ))
                  ) : (
                    <div className="text-gray-500 italic text-sm py-2">No menu items available.</div>
                  )}
                </div>

                <div className="mt-5 border-t pt-5 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
                  {token ? (
                    <div className="space-y-3">
                      <button onClick={() => { setMobileMenu(false); navigate(role === "player" ? "/player-profile" : "/admin/profile"); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl hover:text-emerald-900 hover:bg-emerald-50 transition-colors">Profile</button>
                      <button onClick={() => { setMobileMenu(false); navigate("/coach"); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl text-amber-700 hover:bg-amber-50 transition-colors">Coach Portal</button>
                      <button onClick={() => { setMobileMenu(false); navigate("/change-password"); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl hover:text-emerald-900 hover:bg-emerald-50 transition-colors">Change Password</button>
                      <button onClick={() => { setMobileMenu(false); handleLogout(); }} className="w-full text-left text-lg font-semibold px-3 py-3 rounded-xl text-rose-600 hover:text-rose-700 hover:bg-rose-50 transition-colors">Logout</button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => {
                          setMobileMenu(false);
                          navigate("/login");
                        }}
                        className="text-center text-lg font-semibold text-emerald-900 border-2 border-emerald-400 rounded-xl w-full py-3 hover:bg-emerald-100 transition-all"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => {
                          setMobileMenu(false);
                          navigate("/register");
                        }}
                        className="text-center text-lg font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl w-full py-3 hover:from-emerald-700 hover:to-teal-700 transition-all"
                      >
                        Register as Player
                      </button>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          </div>
        ) : null}
      </header>
    );
  }

  return (
    <header className={HEADER_SHELL_CLASS}>
      {/* ===== ACCESSIBILITY BAR ===== */}
      <div className={`py-2 px-4 border-b ${highContrast ? 'bg-black border-white text-white' : 'bg-slate-100 border-slate-200 text-slate-700'} transition-all`}>
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3 font-sans text-xs font-semibold">
          {/* <div className="hidden md:flex items-center gap-1.5">
            <span role="img" aria-label="accessibility">♿</span>
            <span className="font-extrabold uppercase tracking-wide">Accessibility:</span>
          </div> */}

          <div className="flex flex-wrap items-center gap-3.5">
            {/* Text Size Scale */}
            <div className="flex items-center gap-1">
              <FaTextHeight className="opacity-75" />
              <button
                type="button"
                onClick={() => setTextScale("normal")}
                className={`px-2 py-0.5 rounded text-[10px] md:text-xs transition-all ${textScale === "normal" ? (highContrast ? "bg-white text-black font-black border border-white" : "bg-emerald-600 text-white font-extrabold shadow-sm") : (highContrast ? "bg-black text-white border border-white" : "bg-white hover:bg-slate-200 border border-slate-300 text-slate-800")}`}
              >
                A
              </button>
              <button
                type="button"
                onClick={() => setTextScale("large")}
                className={`px-2 py-0.5 rounded text-[10px] md:text-xs transition-all ${textScale === "large" ? (highContrast ? "bg-white text-black font-black border border-white" : "bg-emerald-600 text-white font-extrabold shadow-sm") : (highContrast ? "bg-black text-white border border-white" : "bg-white hover:bg-slate-200 border border-slate-300 text-slate-800")}`}
              >
                A+
              </button>
              <button
                type="button"
                onClick={() => setTextScale("extra-large")}
                className={`px-2 py-0.5 rounded text-[10px] md:text-xs transition-all ${textScale === "extra-large" ? (highContrast ? "bg-white text-black font-black border border-white" : "bg-emerald-600 text-white font-extrabold shadow-sm") : (highContrast ? "bg-black text-white border border-white" : "bg-white hover:bg-slate-200 border border-slate-300 text-slate-800")}`}
              >
                A++
              </button>
            </div>

            {/* High Contrast Mode */}
            <button
              type="button"
              onClick={() => setHighContrast(!highContrast)}
              className={`flex items-center gap-1 px-2.5 py-0.5 rounded border text-[10px] md:text-xs transition-all ${highContrast ? "bg-yellow-400 text-black border-yellow-400 font-bold" : "bg-white hover:bg-slate-200 border-slate-300 text-slate-800"}`}
            >
              <FaAdjust />
              {highContrast ? "Contrast: On" : "Contrast: Off"}
            </button>

            {/* Text To Speech Read Aloud */}
            <div className="flex items-center gap-1">
              {readingLang ? (
                <>
                  <span className="text-emerald-600 animate-pulse font-extrabold flex items-center gap-1 text-[10px] bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                    <FaVolumeUp className="animate-bounce" /> Reading {readingLang === "en" ? "EN" : "తెలుగు"}
                  </span>
                  <button
                    type="button"
                    onClick={stopReading}
                    className="flex items-center gap-1 px-2 py-0.5 rounded bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-bold transition-all shadow-sm"
                  >
                    <FaVolumeMute /> Stop
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => startReading("en")}
                    className={`flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] transition-all font-bold ${highContrast ? "border border-white text-white hover:bg-white hover:text-black" : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"}`}
                  >
                    🔊 EN
                  </button>
                  <button
                    type="button"
                    onClick={() => startReading("te")}
                    className={`flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] transition-all font-bold ${highContrast ? "border border-white text-white hover:bg-white hover:text-black" : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"}`}
                  >
                    🔊 తెలుగు
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`${HEADER_SURFACE_CLASS} border-t border-emerald-200/80`}>
        <div className="w-full px-2 sm:px-4 lg:px-6 py-2 lg:py-2.5 2xl:py-3.5">
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 lg:gap-4 w-full">

            {/* COLUMN 1: LOGO & TITLE BLOCK */}
            <button
              className="flex items-center justify-start gap-2.5 text-left shrink-0"
              onClick={() => navigate(isLoggedIn ? dashboardPath : "/")}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                alt="Para Sports Association of Andhra Pradesh"
                className="h-12 sm:h-14 lg:h-16 xl:h-16 2xl:h-20 w-auto object-contain shrink-0"
              />
              <div className="min-w-2">
                <p className="text-sm sm:text-base lg:text-lg xl:text-lg 2xl:text-xl font-black leading-none text-slate-900">
                  Para Sports Association of Andhra Pradesh
                </p>
                <p className="mt-1 text-[8.5px] sm:text-[10px] font-bold text-slate-800 uppercase tracking-wide">
                  Affiliated to Paralympic Committee of India
                </p>
                <p className="mt-0.5 text-[8.5px] sm:text-[10px] font-bold text-slate-800 uppercase tracking-wide">
                  Recognized by Sports Authority of Andhra Pradesh
                </p>
              </div>
            </button>

            {/* COLUMN 2: CENTERED NAVIGATION MENU (XL SCREEN ONLY) */}
            <nav className="hidden xl:flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 px-2 py-0.5 max-w-[660px] mx-auto">
              {publicNavItems.map((item, idx) => item.children?.length ? (
                <div key={item.name} className="relative group">
                  <button onClick={() => navigate(item.path)} className={`whitespace-nowrap px-2.5 py-1 rounded-full border text-xs xl:text-[13px] 2xl:text-sm font-extrabold transition-all duration-200 ${item.children.some((child) => isItemActive(child.path)) ? "border-emerald-700 bg-emerald-700 text-white shadow-sm" : "border-emerald-250 bg-white/95 text-slate-700 hover:border-emerald-350 hover:bg-emerald-50 hover:text-emerald-900"}`}>{item.name} <span className="text-[9px]">▾</span></button>
                  <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 absolute z-[100] top-full left-0 pt-2 transition-all"><div className="min-w-48 rounded-xl border border-emerald-100 bg-white p-1.5 shadow-xl">{item.children.map((child) => <button key={child.path} onClick={() => navigate(child.path)} className="block w-full rounded-lg px-3 py-2 text-left text-xs font-bold text-slate-700 hover:bg-emerald-50 hover:text-emerald-900">{child.name}</button>)}</div></div>
                </div>
              ) : <button key={item.path || idx} onClick={() => navigate(item.path)} className={`whitespace-nowrap px-2.5 py-1 rounded-full border text-xs xl:text-[13px] 2xl:text-sm font-extrabold transition-all duration-200 ${isItemActive(item.path) ? "border-emerald-700 bg-emerald-700 text-white shadow-sm" : "border-emerald-250 bg-white/95 text-slate-700 hover:border-emerald-350 hover:bg-emerald-50 hover:text-emerald-900"}`}>{item.name}</button>)}
            </nav>

            {/* COLUMN 3: ACTIONS & PRESIDENT PROFILE */}
            <div className="flex flex-col items-start gap-2 shrink-0 w-full xl:w-auto">

              {/* WebMail link */}
              <a
                href={WEBMAIL_URL}
                target="_blank"
                rel="noreferrer"
                title="Open Webmail"
                className={`${isLoggedIn ? "hidden sm:flex" : "hidden sm:flex xl:hidden"} items-center gap-1.5 rounded-full border border-emerald-200 bg-white/95 px-3 py-1 text-xs font-bold text-slate-700 hover:bg-emerald-100 hover:text-emerald-900 transition-colors`}
              >
                <FaEnvelopeOpenText size={12} />
                <span>WebMail</span>
              </a>

              {/* President Profile info */}
              {!isLoggedIn && (
                <div className="hidden xl:flex items-center gap-2.5 border-emerald-300/40 xl:border-l pl-0 xl:pl-3 shrink-0 mt-2 xl:mt-0 order-2 xl:order-none">
                  <div className="h-14 w-14 sm:h-16 sm:w-16 xl:h-16 xl:w-16 2xl:h-20 2xl:w-20 shrink-0 rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                    <img
                      src={headerPhotoUrl}
                      alt={headerName}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = FALLBACK_HEADER_PROFILE.imageSrc;
                      }}
                    />
                  </div>
                  <div className="text-left">
                    <h4 className="text-xs sm:text-sm lg:text-sm xl:text-base leading-none font-black tracking-tight text-[#5a1c3c]">
                      {headerName}
                    </h4>
                    {resolvedHeaderLines.map((line, index) => (
                      <p
                        key={`${line}-${index}`}
                        className="text-[10px] sm:text-[11px] xl:text-[11.5px] font-extrabold tracking-tight text-[#1d3c73] mt-0.5"
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Login / User Dropdown */}
              {token ? (
                <div className="relative shrink-0 z-50" ref={dropdownRef}>
                  <div
                    onClick={() => setShowDropdown((s) => !s)}
                    className="flex items-center gap-3 cursor-pointer rounded-full border border-emerald-200 bg-white/90 pl-2 pr-3 py-1.5 hover:bg-emerald-100 transition-colors"
                  >
                    <img
                      src={photoUrl}
                      alt="User"
                      className="w-8 h-8 lg:w-9 lg:h-9 rounded-full object-cover border border-emerald-200"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/images/placeholder-user.svg";
                      }}
                    />
                    <div className="text-left hidden sm:block">
                      <div className="text-xs font-bold text-slate-900">{displayName}</div>
                      <div className="text-[10px] text-slate-500 capitalize leading-none">{role}</div>
                    </div>
                  </div>

                  {showDropdown && (
                    <div className="absolute right-0 mt-2 bg-white text-black rounded-2xl shadow-xl w-56 p-2 border border-emerald-200 z-[999]">
                      {/* Role-based dashboard shortcuts */}
                      {role === "player" && (
                        <>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/player"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            🏠 Player Dashboard
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/register-event"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            📝 Register Event
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/certificates"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            🏅 My Certificates
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/coach/login?switch=true"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-amber-50 text-amber-900 transition-colors text-xs font-bold flex items-center justify-between border border-amber-200/50 mt-1"
                          >
                            <span>🛡️ Coach Portal</span>
                            <span className="text-[9px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-black">SWITCH</span>
                          </button>
                        </>
                      )}
                      {role === "coach" && (
                        <>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/coach"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            🛡️ Coach Dashboard
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/coach/athletes"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            👥 Camp Athletes
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/coach/daily-reports"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            📋 Daily Reports
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/login?switch=true"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-indigo-50 text-indigo-900 transition-colors text-xs font-bold flex items-center justify-between border border-indigo-200/50 mt-1"
                          >
                            <span>🏃 Player Portal</span>
                            <span className="text-[9px] bg-indigo-200 text-indigo-900 px-1.5 py-0.5 rounded font-black">SWITCH</span>
                          </button>
                        </>
                      )}
                      {role === "district" && (
                        <>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/district"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            🏠 District Dashboard
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/district/certificates/issued"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            📋 Issued Certificates
                          </button>
                        </>
                      )}
                      {["admin", "secretary"].includes(role) && (
                        <>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/admin"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            🏠 Admin Dashboard
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/admin/achievements"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            🏅 Achievements
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/admin/certificates/signature"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            ✍️ Signatures Manager
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/admin/mail-center"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            ✉️ Mail Center
                          </button>
                          <button
                            onClick={() => { setShowDropdown(false); navigate("/admin/announcements"); }}
                            className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                          >
                            📢 Ticker Alerts
                          </button>
                        </>
                      )}
                      <hr className="my-1.5 border-slate-100" />
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          if (role === "player") navigate("/player-profile");
                          else if (role === "district") navigate("/district/profile");
                          else if (["admin", "secretary"].includes(role)) navigate("/admin/profile");
                          else navigate("/player-profile");
                        }}
                        className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                      >
                        👤 Profile
                      </button>
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          navigate("/change-password");
                        }}
                        className="w-full text-left px-4 py-2 rounded-xl hover:bg-emerald-100 transition-colors text-xs font-bold text-slate-700"
                      >
                        🔑 Change Password
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 rounded-xl hover:bg-rose-50 text-rose-600 transition-colors text-xs font-bold"
                      >
                        🚪 Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : null}

            </div>
          </div>
        </div>
      </div>

      {/* Desktop Login/Register, WebMail & Excellence Awards Ticker Row */}
      <div className="hidden xl:block border-t border-emerald-300 bg-white/40 py-2.5 px-4 lg:px-6 shadow-sm">
        <div className="w-full px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between w-full gap-6">

            {/* Left Side: Scrolling Ticker (Excellence Awards & Announcements) */}
            <div
              className="flex-1 max-w-[calc(100%-470px)] flex items-center gap-2.5 overflow-hidden bg-emerald-50/70 border border-emerald-100 rounded-full px-4 py-1.5 transition-all duration-200"
            >
              <span className="animate-pulse flex items-center shrink-0 text-emerald-700 font-extrabold text-sm tracking-wider">
                🏆 Updates:
              </span>
              <div className="relative w-full overflow-hidden whitespace-nowrap text-sm font-black text-slate-700">
                <div
                  className="inline-block animate-marquee-custom"
                  style={{
                    display: "inline-block",
                    animation: `marquee-scroll ${Math.max(25, (announcements.length || 1) * 20)}s linear infinite`,
                    paddingLeft: "100%"
                  }}
                >
                  {announcements.length > 0 ? (
                    announcements.map((item, idx) => (
                      <span
                        key={item.id || idx}
                        onClick={(e) => handleAnnounceClick(e, item)}
                        className="inline-block hover:underline text-slate-700 cursor-pointer mr-32"
                      >
                        🔥 {item.text}
                      </span>
                    ))
                  ) : (
                    <span
                      onClick={() => navigate("/excellence-awards")}
                      className="inline-block hover:underline text-slate-700 cursor-pointer text-sm font-black"
                    >
                      Apply now for the Parasports Excellence Awards! Click here to check guidelines, view live applicant statistics, and submit your achievement details.
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side: WebMail / Login / Register */}
            {!isLoggedIn ? (
              <div className="w-[450px] lg:w-[520px] flex items-center justify-start gap-2 pl-4 shrink-0">
                <a
                  href={WEBMAIL_URL}
                  target="_blank"
                  rel="noreferrer"
                  title="Open Webmail"
                  className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white/95 px-3 py-2 text-xs xl:text-sm font-extrabold text-slate-700 hover:bg-emerald-100 hover:text-emerald-900 transition-colors shrink-0"
                >
                  <FaEnvelopeOpenText size={13} />
                  <span>WebMail</span>
                </a>
                <button
                  onClick={() => navigate("/login")}
                  className="font-extrabold text-emerald-900 border border-emerald-300 bg-emerald-100 rounded-full px-3.5 py-2 text-xs xl:text-sm hover:bg-emerald-200 transition-colors shrink-0"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="font-extrabold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full px-4 py-2 text-xs xl:text-sm hover:from-emerald-700 hover:to-teal-700 shadow-sm transition-all duration-300 shrink-0"
                >
                  Register
                </button>
              </div>
            ) : (
              // Empty spacer to keep layout balanced when logged in
              <div className="w-[100px] shrink-0" />
            )}
          </div>
        </div>

        <style>{`
          @keyframes marquee-scroll {
            0% { transform: translate3d(0, 0, 0); }
            100% { transform: translate3d(-100%, 0, 0); }
          }
          .animate-marquee-custom:hover {
            animation-play-state: paused !important;
          }
        `}</style>
      </div>

      {/* Mobile/Tablet Scrolling Ticker */}
      <div className="block xl:hidden border-t border-emerald-250 bg-emerald-50/60 py-1.5 px-4 shadow-sm">
        <div className="flex items-center gap-2 overflow-hidden w-full">
          <span className="animate-pulse shrink-0 text-emerald-700 font-extrabold text-[10px] uppercase tracking-wider">
            🏆 Updates:
          </span>
          <div className="relative w-full overflow-hidden whitespace-nowrap text-xs font-black text-slate-700">
            <div
              className="inline-block animate-marquee-custom"
              style={{
                display: "inline-block",
                animation: `marquee-scroll ${Math.max(25, (announcements.length || 1) * 20)}s linear infinite`,
                paddingLeft: "100%"
              }}
            >
              {announcements.length > 0 ? (
                announcements.map((item, idx) => (
                  <span
                    key={item.id || idx}
                    onClick={(e) => handleAnnounceClick(e, item)}
                    className="inline-block hover:underline text-slate-700 cursor-pointer mr-20"
                  >
                    🔥 {item.text}
                  </span>
                ))
              ) : (
                <span
                  onClick={() => navigate("/excellence-awards")}
                  className="inline-block hover:underline text-slate-700 cursor-pointer"
                >
                  Apply now for the Parasports Excellence Awards! Click here to check guidelines and submit details.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE NAVIGATION TITLE BAR */}
      <div className="xl:hidden border-t border-emerald-300 bg-white">
        <div className="w-full px-4 pt-1.5 pb-3 flex items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="h-12 w-12 shrink-0 rounded-2xl overflow-hidden border border-emerald-300 bg-white shadow-sm">
              <img
                src={headerPhotoUrl}
                alt={headerName}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = FALLBACK_HEADER_PROFILE.imageSrc;
                }}
              />
            </div>
            <div className="text-left min-w-0">
              <h4 className="text-[13.5px] font-black text-[#5a1c3c] truncate">
                {headerName}
              </h4>
              <p className="text-[10px] font-extrabold text-[#1d3c73] leading-tight truncate mt-0.5">
                {resolvedHeaderLines[0]}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            <button
              className="text-slate-700 bg-white border border-emerald-250 rounded-xl p-2.5 shadow-sm flex items-center justify-center"
              onClick={() => setMobileMenu((prev) => !prev)}
              aria-label={mobileMenu ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenu}
            >
              {mobileMenu ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenu ? (
        <div className="xl:hidden fixed inset-0 z-[9999] bg-white">
          <div className="h-full flex flex-col">
            <div className={`${HEADER_SURFACE_CLASS} shrink-0 border-b border-emerald-200 px-4 py-3 flex items-center justify-between`}>
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={`${process.env.PUBLIC_URL}/images/parasports.png`}
                  alt="Logo"
                  className="h-10 w-10 rounded-full object-cover border border-emerald-200"
                />
                <div className="min-w-0">
                  <h2 className="text-xl font-extrabold text-slate-900 truncate">
                    {isLoggedIn ? "Menu" : headerName}
                  </h2>
                  <p className="text-xs text-slate-700 truncate">
                    {isLoggedIn ? "Navigation" : resolvedHeaderLines[0] || "Site menu"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMobileMenu(false)}
                aria-label="Close menu"
                className="rounded-lg p-2 hover:bg-emerald-100 transition-colors"
              >
                <FaTimes size={22} />
              </button>
            </div>

            <nav id="mobile-nav-drawer" className="flex flex-col flex-1 min-h-0 overflow-y-auto px-4 py-4 pb-20">
              {/* President profile is displayed under logo directly, no need to show inside drawer */}

              <div className="flex flex-col gap-1">
                {navItems && navItems.length > 0 ? (
                  publicNavItems.map((item, idx) => (
                    <React.Fragment key={item.path || `${item.name || "menu"}-${idx}`}>
                      <button
                        onClick={() => {
                          setMobileMenu(false);
                          navigate(item.path);
                        }}
                        className="w-full text-left text-lg font-semibold py-2 px-3 rounded-xl border border-transparent hover:border-teal-200 hover:text-teal-900 hover:bg-teal-50 transition-colors"
                      >
                        {item.name}
                      </button>
                      {item.children?.map((child) => <button key={child.path} onClick={() => { setMobileMenu(false); navigate(child.path); }} className="w-full text-left text-base font-medium py-2 pl-8 pr-3 rounded-xl text-slate-600 hover:text-teal-900 hover:bg-teal-50">{child.name}</button>)}
                    </React.Fragment>
                  ))
                ) : (
                  <div className="text-gray-500 italic text-sm py-2">No menu items available.</div>
                )}
              </div>

              <div className="order-first mb-4 border-b pb-5">
                <a
                  href={WEBMAIL_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setMobileMenu(false)}
                  className="w-full flex items-center justify-center gap-2 text-center text-lg font-semibold py-3 mb-3 rounded-xl border-2 border-teal-300 text-teal-900 hover:bg-teal-50 transition-colors"
                >
                  <FaEnvelopeOpenText size={18} /> Mail
                </a>
                {token ? (
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => { setMobileMenu(false); navigate(role === "player" ? "/player-profile" : (role === "district" ? "/district/profile" : "/admin/profile")); }} className="w-full text-sm font-semibold px-2 py-2 rounded-xl border border-teal-200 text-teal-900">Profile</button>
                    <button onClick={() => { setMobileMenu(false); navigate("/change-password"); }} className="w-full text-sm font-semibold px-2 py-2 rounded-xl border border-teal-200 text-teal-900">Password</button>
                    <button onClick={() => { setMobileMenu(false); handleLogout(); }} className="w-full text-sm font-semibold px-2 py-2 rounded-xl border border-rose-200 text-rose-700">Logout</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setMobileMenu(false);
                        navigate("/login");
                      }}
                      className="text-center text-base font-semibold text-teal-900 border-2 border-teal-300 rounded-xl w-full py-3 hover:bg-teal-50 transition-all"
                    >
                      Login (Player / Official)
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenu(false);
                        navigate("/register");
                      }}
                      className="text-center text-base font-semibold text-white bg-gradient-to-r from-teal-700 to-cyan-700 rounded-xl w-full py-3 hover:from-teal-800 hover:to-cyan-800 transition-all"
                    >
                      Register as Player
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      ) : null}
    </header>
  );
};

export default Navbar;
